import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';

const QuoteBuilder = () => {
    const [customers, setCustomers] = useState([]);
    const [executives, setExecutives] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [itemsList, setItemsList] = useState([]);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const editId = searchParams.get('id');

    const [quoteData, setQuoteData] = useState({
        customer_name: '',
        executive_id: '',
        quote_number: `QNZ-${Math.floor(Math.random() * 10000)}`,
        contact_person: '',
        date: new Date().toISOString().split('T')[0],
        terms: `1. Validity: This quotation is valid for 30 days.
2. Payment: 50% advance, 50% on completion.
3. Delivery: Within 7 working days from the date of PO.
4. VAT: Prices are exclusive of 5% VAT.`,
    });

    const [termsMode, setTermsMode] = useState('none'); // 'none', 'view', 'edit'

    const [selectedItems, setSelectedItems] = useState([
        { item_id: '', qty: 1, rate: 0, default_rate: 0, amount: 0 }
    ]);
    const [grandTotal, setGrandTotal] = useState(0);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [custRes, itemRes, execRes] = await Promise.all([
                    api.get('/customers'),
                    api.get('/items'),
                    api.get('/executives')
                ]);
                setCustomers(custRes.data.data);
                setItemsList(itemRes.data.data);
                console.log('Fetching Executives:', execRes.data.data);
                setExecutives(execRes.data.data);

                // Hardcoded templates for now
                setTemplates([
                    { id: 1, name: 'Standard Template' },
                    { id: 2, name: 'Premium Template' },
                    { id: 3, name: 'Modern Template' }
                ]);

                // If in edit mode, fetch the quote details
                if (editId) {
                    const quoteRes = await api.get(`/quotes/${editId}`);
                    const q = quoteRes.data.data;

                    setQuoteData({
                        id: q.id,
                        customer_name: q.customer_name,
                        customer_id: q.customer_id?._id || q.customer_id,
                        executive_id: q.executive_id?._id || q.executive_id,
                        quote_number: q.quote_number,
                        contact_person: q.contact_person,
                        date: new Date(q.date || q.created_at).toISOString().split('T')[0],
                        terms: q.terms,
                        template_id: q.template_id?._id || q.template_id
                    });

                    if (q.items && q.items.length > 0) {
                        const items = q.items.map(item => ({
                            item_id: item.item_id?._id || item.item_id,
                            qty: Number(item.qty),
                            rate: Number(item.rate),
                            default_rate: Number(item.rate), // Best guess for default_rate when editing
                            amount: Number(item.amount)
                        }));
                        setSelectedItems(items);
                        calculateGrandTotal(items);
                    }
                }

            } catch (err) {
                console.error('Failed to fetch initial data', err);
            }
        };
        fetchInitialData();
    }, [editId]);

    const handleQuoteDataChange = (e) => {
        setQuoteData({ ...quoteData, [e.target.name]: e.target.value });
    };

    const handleCustomerChange = (e) => {
        const custId = e.target.value;
        const customer = customers.find(c => (c._id || c.id) == custId);
        if (customer) {
            setQuoteData({
                ...quoteData,
                customer_id: custId,
                customer_name: customer.company_name,
                contact_person: customer.contact_person || ''
            });
        } else {
            setQuoteData({
                ...quoteData,
                customer_id: '',
                customer_name: '',
                contact_person: ''
            });
        }
    };

    const handleItemChange = (idx, field, value) => {
        const newItems = [...selectedItems];
        newItems[idx][field] = value;

        if (field === 'item_id') {
            // Use == (loose) because select value is a string but MySQL id is a number
            const selectedItem = itemsList.find(i => (i._id || i.id) == value);
            if (selectedItem) {
                const rate = parseFloat(selectedItem.default_rate || 0);
                newItems[idx].default_rate = rate;
                newItems[idx].rate = rate;
            }
        }

        newItems[idx].amount = newItems[idx].qty * newItems[idx].rate;
        setSelectedItems(newItems);
        calculateGrandTotal(newItems);
    };

    const addRow = () => {
        setSelectedItems([...selectedItems, { item_id: '', qty: 1, rate: 0, default_rate: 0, amount: 0 }]);
    };

    const removeRow = (idx) => {
        const newItems = selectedItems.filter((_, i) => i !== idx);
        setSelectedItems(newItems);
        calculateGrandTotal(newItems);
    };

    const calculateGrandTotal = (items) => {
        const total = items.reduce((sum, item) => sum + item.amount, 0);
        setGrandTotal(total);
    };

    const handleSave = async () => {
        if (!quoteData.customer_name || !quoteData.executive_id) {
            alert('Please enter Customer and select Executive');
            return;
        }

        const payload = {
            ...quoteData,
            total_amount: grandTotal,
            items: selectedItems
        };

        try {
            const res = await api.post('/quotes', payload);
            if (res.data.status === 'success') {
                alert('Quotation saved successfully!');
                navigate(`/quotes/${res.data.id}`);
            }
        } catch (err) {
            console.error('Failed to save quotation', err);
            alert('Error saving quotation');
        }
    };

    return (
        <div className="p-4 md:p-10 max-w-[1400px] mx-auto">
            <h1 className="text-2xl md:text-[2.2rem] font-[900] text-primary mb-10 tracking-tight">
                {editId ? 'Edit Quotation' : 'Generate New Quotation'}
            </h1>

            {/* 1. Quote Meta */}
            <div className="bg-white rounded-2xl border-2 border-slate-200 mb-10 overflow-hidden shadow-sm">
                <div className="section-header">
                    <h3>1. Quote Meta</h3>
                </div>
                <div className="bg-slate-50/10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 meta-grid">
                    {/* Row 1 */}
                    <div className="p-8 group hover:bg-slate-50/50 transition-colors border-r-2 border-b-2 border-slate-100/50">
                        <label className="block text-[11px] font-[800] text-slate-400 mb-3 uppercase tracking-[0.1em]">Quote Number</label>
                        <input value={quoteData.quote_number} readOnly className="input-field bg-slate-100 font-bold text-slate-600 border-none shadow-none" />
                    </div>

                    <div className="p-8 group hover:bg-slate-50/50 transition-colors border-r-2 border-b-2 border-slate-100/50">
                        <label className="block text-[11px] font-[800] text-slate-400 mb-3 uppercase tracking-[0.1em]">Quotation Date</label>
                        <input type="date" name="date" value={quoteData.date} onChange={handleQuoteDataChange} className="input-field font-bold text-primary" />
                    </div>

                    <div className="p-8 group hover:bg-slate-50/50 transition-colors lg:col-span-2 border-b-2 border-slate-100/50">
                        <label className="block text-[11px] font-[800] text-slate-400 mb-3 uppercase tracking-[0.1em]">Customer Name / Company</label>
                        <input name="customer_name" value={quoteData.customer_name} onChange={handleQuoteDataChange} placeholder="e.g. Acme Corp" className="input-field font-bold text-slate-800" />
                    </div>

                    {/* Row 2 */}
                    <div className="p-8 group hover:bg-slate-50/50 transition-colors lg:col-span-2 border-r-2 border-slate-100/50">
                        <label className="block text-[11px] font-[800] text-slate-400 mb-3 uppercase tracking-[0.1em]">Sales Executive</label>
                        <select name="executive_id" value={quoteData.executive_id} onChange={handleQuoteDataChange} className="input-field font-bold text-primary">
                            <option value="">- Select -</option>
                            {executives.map(e => <option key={e._id || e.id} value={e._id || e.id}>{e.name}</option>)}
                        </select>
                    </div>

                    <div className="p-8 group hover:bg-slate-50/50 transition-colors lg:col-span-2">
                        <label className="block text-[11px] font-[800] text-slate-400 mb-3 uppercase tracking-[0.1em]">Contact Person</label>
                        <input name="contact_person" value={quoteData.contact_person} onChange={handleQuoteDataChange} placeholder="e.g. John Doe" className="input-field font-bold text-slate-800" />
                    </div>
                </div>
            </div>

            {/* 2. Particulars */}
            <div className="bg-white rounded-2xl border-2 border-slate-200 overflow-hidden mb-10 shadow-sm">
                <div className="section-header">
                    <h3>2. Particulars & Investment</h3>
                </div>
                <div className="p-0 bg-slate-50/10">
                    <div className="overflow-x-auto">
                        <table className="data-table !border-none !rounded-none">
                            <thead>
                                <tr>
                                    <th className="!py-6 w-[35%]">Particular Account</th>
                                    <th className="!py-6 w-[15%]">Default Rate</th>
                                    <th className="!py-6 w-[15%]">Rate (Edit)</th>
                                    <th className="!py-6 w-[10%]">Quantity</th>
                                    <th className="!py-6 w-[15%]">Total Amount</th>
                                    <th className="!py-6 w-[10%] text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedItems.map((item, idx) => (
                                    <tr key={idx}>
                                        <td data-label="Particular" className="!py-6">
                                            <select value={item.item_id} onChange={(e) => handleItemChange(idx, 'item_id', e.target.value)} className="input-field font-bold text-primary">
                                                <option value="">- Select Item -</option>
                                                {itemsList.map(i => <option key={i._id || i.id} value={i._id || i.id}>{i.name}</option>)}
                                            </select>
                                        </td>
                                        <td data-label="Default Rate" className="!py-6 font-medium text-slate-400">AED {parseFloat(item.default_rate || 0).toFixed(2)}</td>
                                        <td data-label="Rate Edit" className="!py-6">
                                            <input type="number" value={item.rate} onChange={(e) => handleItemChange(idx, 'rate', parseFloat(e.target.value) || 0)} className="input-field font-bold text-slate-700" />
                                        </td>
                                        <td data-label="Qty" className="!py-6">
                                            <input type="number" value={item.qty} min="1" onChange={(e) => handleItemChange(idx, 'qty', parseInt(e.target.value) || 1)} className="input-field font-bold text-slate-700 text-center" />
                                        </td>
                                        <td className="!py-6 font-[900] text-primary" data-label="Total">AED {item.amount?.toFixed(2)}</td>
                                        <td className="!py-6 text-center" data-label="Action">
                                            <button
                                                onClick={() => removeRow(idx)}
                                                className="p-3 bg-red-50 text-red-600 rounded-xl hover:bg-red-600 hover:text-white transition-all border border-red-100"
                                                title="Remove Line"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /><line x1="10" x2="10" y1="11" y2="17" /><line x1="14" x2="14" y1="11" y2="17" /></svg>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {selectedItems.length === 0 && (
                                    <tr><td colSpan="6" className="text-center p-16 text-slate-400 font-bold italic">No items added to this quotation</td></tr>
                                )}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan="6" className="!p-8 !border-none bg-slate-50/5/30">
                                        <button className="flex items-center gap-2 bg-white text-slate-700 font-[800] py-3.5 px-8 rounded-xl hover:bg-slate-100 transition-all border-2 border-slate-200 shadow-sm" onClick={addRow}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="M12 5v14" /></svg>
                                            Add New Line
                                        </button>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    <div className="flex flex-wrap justify-end items-center gap-10 p-10 border-t-2 border-slate-100 bg-white">
                        <div className="text-right">
                            <div className="text-[11px] text-slate-400 font-[800] mb-2 uppercase tracking-[0.15em]">Grand Total (Excl. VAT)</div>
                            <div className="text-[2.5rem] font-[900] text-primary leading-none tracking-tight">AED {grandTotal.toFixed(2)}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 3. Terms and Conditions */}
            <div className="bg-white rounded-2xl border-2 border-slate-200 mb-12 overflow-hidden shadow-sm">
                <div className="section-header">
                    <h3>3. Terms and Conditions</h3>
                    <div className="flex gap-4">
                        <button
                            className={`px-6 py-2 rounded-xl text-xs font-[800] uppercase tracking-wider transition-all ${termsMode === 'view' ? 'bg-primary text-white shadow-md' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                            onClick={() => setTermsMode(termsMode === 'view' ? 'none' : 'view')}
                        >
                            Preview Terms
                        </button>
                        <button
                            className={`px-6 py-2 rounded-xl text-xs font-[800] uppercase tracking-wider transition-all ${termsMode === 'edit' ? 'bg-primary text-white shadow-md' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                            onClick={() => setTermsMode(termsMode === 'edit' ? 'none' : 'edit')}
                        >
                            Modify Terms
                        </button>
                    </div>
                </div>

                <div className={`transition-all duration-300 ${termsMode === 'none' ? 'h-0 opacity-0 overflow-hidden' : 'p-10 bg-slate-50/10'}`}>
                    {termsMode === 'view' && (
                        <div className="p-10 bg-white rounded-2xl border-2 border-slate-100 relative group shadow-inner">
                            <pre className="whitespace-pre-wrap font-sans text-slate-600 leading-relaxed text-[15px] font-medium">
                                {quoteData.terms}
                            </pre>
                            <button
                                className="absolute top-6 right-6 p-2.5 text-slate-300 hover:text-primary transition-all bg-slate-50/50 rounded-xl hover:bg-white hover:shadow-sm"
                                title="Edit Terms"
                                onClick={() => setTermsMode('edit')}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" /><path d="m15 5 4 4" /></svg>
                            </button>
                        </div>
                    )}

                    {termsMode === 'edit' && (
                        <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-top-4 duration-300">
                            <textarea
                                name="terms"
                                value={quoteData.terms}
                                onChange={handleQuoteDataChange}
                                className="input-field min-h-[250px] font-sans text-[15px] p-6 bg-white leading-relaxed resize-y"
                                placeholder="Enter terms and conditions..."
                            />
                            <div className="flex justify-end">
                                <button
                                    className="px-10 py-3.5 bg-slate-900 text-white rounded-xl text-sm font-[800] hover:bg-slate-800 transition-all shadow-lg flex items-center gap-2"
                                    onClick={() => setTermsMode('view')}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                                    Apply Changes
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Save Block */}
            <div className="flex justify-end pt-4 mb-20">
                <button
                    className="group relative flex items-center gap-3 bg-primary text-white font-[900] py-5 px-20 rounded-2xl hover:scale-[1.02] active:scale-[0.98] transition-all shadow-xl shadow-primary/20 overflow-hidden"
                    onClick={handleSave}
                >
                    <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>
                    <span className="text-xl tracking-tight">Generate & Save Quotation</span>
                </button>
            </div>
        </div>
    );
};

export default QuoteBuilder;
